import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Pizza } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ORDER_URL = 'https://wowpizza.world/';

function setPageMeta({ title, description, canonical, schema }) {
  document.title = title;

  let metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) metaDesc.setAttribute('content', description);

  let link = document.querySelector('link[rel="canonical"]');
  if (link) link.setAttribute('href', canonical);

  const existing = document.getElementById('page-schema');
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.id = 'page-schema';
  script.type = 'application/ld+json';
  script.text = JSON.stringify(schema);
  document.head.appendChild(script);
}

function LocationPage({ branch }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  useEffect(() => {
    setPageMeta({
      title: branch.metaTitle,
      description: branch.metaDescription,
      canonical: branch.url,
      schema: branch.schema,
    });
    return () => {
      // restore base title on unmount
      document.title = 'Wow Pizza Oman | Pizza Delivery in Amerat, Qurum & Al Khuwair | Order Online';
      const el = document.getElementById('page-schema');
      if (el) el.remove();
    };
  }, [branch]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-red-600 text-white py-12 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-center mb-4">
            <Pizza size={48} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{lang === 'ar' && branch.h1_ar ? branch.h1_ar : branch.h1}</h1>
          <p className="text-lg text-red-100">{lang === 'ar' && branch.tagline_ar ? branch.tagline_ar : branch.tagline}</p>
          <a
            href={ORDER_URL}
            className="inline-block mt-6 bg-white text-red-600 font-bold px-8 py-3 rounded-full hover:bg-red-50 transition-colors"
          >
            {t('location.orderNow')}
          </a>
        </div>
      </div>

      {/* Info cards */}
      <div className="max-w-3xl mx-auto px-4 py-10 grid gap-5 md:grid-cols-3">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-start gap-3">
            <MapPin className="text-red-600 mt-0.5 shrink-0" size={20} />
            <div>
              <p className="font-semibold text-gray-900 mb-1">{t('location.address')}</p>
              <p className="text-gray-600 text-sm leading-relaxed">{lang === 'ar' && branch.address_ar ? branch.address_ar : branch.address}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-start gap-3">
            <Phone className="text-red-600 mt-0.5 shrink-0" size={20} />
            <div>
              <p className="font-semibold text-gray-900 mb-1">{t('location.phone')}</p>
              {branch.phones.map((p) => (
                <a key={p.raw} href={`tel:${p.raw}`} className="block text-red-600 text-sm hover:underline">
                  {p.display}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-start gap-3">
            <Clock className="text-red-600 mt-0.5 shrink-0" size={20} />
            <div>
              <p className="font-semibold text-gray-900 mb-1">{t('location.openingHours')}</p>
              <p className="text-gray-600 text-sm leading-relaxed">{t('location.hours')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* About this branch */}
      <div className="max-w-3xl mx-auto px-4 pb-8">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-3">{lang === 'ar' ? 'عن هذا الفرع' : 'About This Branch'}</h2>
          {branch.description.map((para, i) => (
            <p key={i} className="text-gray-600 leading-relaxed mb-3 last:mb-0">
              {para}
            </p>
          ))}
        </div>
      </div>

      {/* Google Map embed */}
      <div className="max-w-3xl mx-auto px-4 pb-10">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <iframe
            title={`Map — ${branch.name}`}
            src={branch.mapEmbed}
            width="100%"
            height="300"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>

      {/* CTA */}
      <div className="bg-red-600 text-white py-10 px-4 text-center">
        <h2 className="text-2xl font-bold mb-3">{lang === 'ar' ? 'جاهز للطلب؟' : 'Ready to Order?'}</h2>
        <p className="text-red-100 mb-6">{lang === 'ar' ? 'اطلب أونلاين للتوصيل السريع إلى بابك.' : 'Order online for fast delivery to your door.'}</p>
        <a
          href={ORDER_URL}
          className="inline-block bg-white text-red-600 font-bold px-8 py-3 rounded-full hover:bg-red-50 transition-colors"
        >
          {t('location.orderNow')}
        </a>
        <p className="mt-4 text-red-200 text-sm">
          {lang === 'ar' ? 'أو اتصل بنا:' : 'Or call us:'}{' '}
          {branch.phones.map((p, i) => (
            <span key={p.raw}>
              {i > 0 && ' / '}
              <a href={`tel:${p.raw}`} className="underline hover:text-white">
                {p.display}
              </a>
            </span>
          ))}
        </p>
      </div>

      {/* Back link */}
      <div className="text-center py-6">
        <Link to="/" className="text-red-600 hover:underline text-sm">
          {lang === 'ar' ? '→ العودة إلى الرئيسية' : '← Back to Wow Pizza Home'}
        </Link>
      </div>
    </div>
  );
}

// ─── Branch definitions ───────────────────────────────────────────────────────

const AMERAT_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Restaurant',
  '@id': 'https://wowpizza.world/#amerat',
  name: 'Wow Pizza — Al Amerat',
  url: 'https://wowpizza.world/amerat',
  telephone: '+96899236352',
  image: 'https://wowpizza.world/assets/logo/mylogo.png',
  servesCuisine: ['Pizza', 'Italian', 'Fast Food'],
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Al Amerat Phase 5, between Omani Shiwa Restaurant & Red Roaster',
    addressLocality: 'Al Amerat',
    addressRegion: 'Muscat',
    addressCountry: 'OM',
  },
  geo: { '@type': 'GeoCoordinates', latitude: 23.588, longitude: 58.639 },
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'], opens: '11:00', closes: '04:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Friday', opens: '13:00', closes: '04:00' },
  ],
  hasMap: 'https://maps.google.com/?q=Wow+Pizza+Al+Amerat+Oman',
};

const QURUM_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Restaurant',
  '@id': 'https://wowpizza.world/#qurum',
  name: 'Wow Pizza — Qurum',
  url: 'https://wowpizza.world/qurum',
  telephone: '+96892111965',
  image: 'https://wowpizza.world/assets/logo/mylogo.png',
  servesCuisine: ['Pizza', 'Italian', 'Fast Food'],
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Qurum',
    addressLocality: 'Qurum',
    addressRegion: 'Muscat',
    addressCountry: 'OM',
  },
  geo: { '@type': 'GeoCoordinates', latitude: 23.6, longitude: 58.552 },
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'], opens: '11:00', closes: '04:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Friday', opens: '13:00', closes: '04:00' },
  ],
  hasMap: 'https://maps.google.com/?q=Wow+Pizza+Qurum+Oman',
};

const KHUWAIR_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Restaurant',
  '@id': 'https://wowpizza.world/#khuwair',
  name: 'Wow Pizza — Al Khuwair',
  url: 'https://wowpizza.world/al-khuwair',
  telephone: '+96892111946',
  image: 'https://wowpizza.world/assets/logo/mylogo.png',
  servesCuisine: ['Pizza', 'Italian', 'Fast Food'],
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Al Khuwair',
    addressLocality: 'Al Khuwair',
    addressRegion: 'Muscat',
    addressCountry: 'OM',
  },
  geo: { '@type': 'GeoCoordinates', latitude: 23.609, longitude: 58.519 },
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'], opens: '11:00', closes: '04:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Friday', opens: '13:00', closes: '04:00' },
  ],
  hasMap: 'https://maps.google.com/?q=Wow+Pizza+Al+Khuwair+Oman',
};

export function AmeratPage() {
  return (
    <LocationPage
      branch={{
        name: 'Wow Pizza — Al Amerat',
        h1: 'Wow Pizza — Pizza Delivery in Al Amerat',
        h1_ar: 'WOW! بيتزا — توصيل البيتزا في العامرات',
        tagline: 'Serving Amerat Phase 3, Phase 4, Phase 5 and beyond — fresh from the oven to your door.',
        tagline_ar: 'نخدم العامرات المرحلة 3 و4 و5 وما بعدها — طازجة من الفرن إلى بابك.',
        metaTitle: 'Wow Pizza Al Amerat | Pizza Delivery in Amerat, Muscat | wowpizza.world',
        metaDescription: 'Order pizza delivery in Al Amerat, Muscat from Wow Pizza. Fresh, oven-baked pizzas delivered to Amerat Phase 3, 4, and 5. Call +968 9923 6352 or order online.',
        url: 'https://wowpizza.world/amerat',
        address: 'Al Amerat Phase 5, between Omani Shiwa Restaurant & Red Roaster, Muscat, Oman',
        address_ar: 'العامرات المرحلة 5، بين مطعم العُماني شيوا ومقهى ريد روستر، مسقط، عُمان',
        phones: [
          { display: '+968 9923 6352', raw: '+96899236352' },
          { display: '24870085', raw: '+96824870085' },
        ],
        mapEmbed: 'https://maps.google.com/maps?q=Al+Amerat+Phase+5+Muscat+Oman&output=embed',
        schema: AMERAT_SCHEMA,
        description: [
          'Al Amerat is one of the fastest-growing neighbourhoods in Muscat, and Wow Pizza opened here to bring real, quality pizza to the community. Our Amerat branch is located in Phase 5, right between Omani Shiwa Restaurant and Red Roaster — easy to find, with ample parking nearby.',
          'We are the go-to pizza spot for families, students, and working professionals across Al Amerat Phase 3, Phase 4, Phase 5, and the surrounding areas. Whether you\'re craving a classic Margherita, loaded pepperoni, or our house special, every pizza is made fresh to order.',
          'Order online for doorstep delivery, or call us directly on +968 9923 6352. We are open Saturday through Thursday from 11:00 AM until 4:00 AM, and on Fridays from 1:00 PM until 4:00 AM — so whether it\'s lunch, dinner, or a late-night craving, we\'ve got you covered.',
        ],
      }}
    />
  );
}

export function QurumPage() {
  return (
    <LocationPage
      branch={{
        name: 'Wow Pizza — Qurum',
        h1: 'Wow Pizza — Pizza Delivery in Qurum',
        h1_ar: 'WOW! بيتزا — توصيل البيتزا في القرم',
        tagline: 'Fresh, oven-baked pizza delivered across Qurum, Ruwi, and Wattayah.',
        tagline_ar: 'بيتزا طازجة من الفرن تُوصَّل عبر القرم والروي والوطية.',
        metaTitle: 'Wow Pizza Qurum | Pizza Delivery in Qurum, Muscat | wowpizza.world',
        metaDescription: 'Order pizza delivery in Qurum, Muscat from Wow Pizza. Oven-fresh pizzas delivered fast across Qurum and nearby areas. Call +968 92111965 or order online.',
        url: 'https://wowpizza.world/qurum',
        address: 'Qurum, Muscat, Oman',
        address_ar: 'القرم، مسقط، عُمان',
        phones: [{ display: '+968 92111965', raw: '+96892111965' }],
        mapEmbed: 'https://maps.google.com/maps?q=Qurum+Muscat+Oman&output=embed',
        schema: QURUM_SCHEMA,
        description: [
          'Qurum is one of Muscat\'s most popular and vibrant neighbourhoods — home to Qurum Natural Park, the beach, and a thriving food scene. Wow Pizza Qurum brings the same oven-fresh, hand-crafted pizzas that made us famous in Amerat right to the heart of Qurum.',
          'Whether you\'re ordering after an evening walk by the park, feeding the family at home, or grabbing a quick dinner, we have something for everyone. Our menu covers all the classics — from Margherita and pepperoni to loaded meat feasts and vegetarian options — all made fresh to order.',
          'We deliver across Qurum and the surrounding areas including Ruwi and Wattayah. Order online for the fastest experience, or call +968 92111965. Open Saturday through Thursday 11:00 AM – 4:00 AM and Friday 1:00 PM – 4:00 AM.',
        ],
      }}
    />
  );
}

export function KhuwairPage() {
  return (
    <LocationPage
      branch={{
        name: 'Wow Pizza — Al Khuwair',
        h1: 'Wow Pizza — Pizza Delivery in Al Khuwair',
        h1_ar: 'WOW! بيتزا — توصيل البيتزا في الخوير',
        tagline: 'Office lunches, family dinners, late-night cravings — delivered across Al Khuwair.',
        tagline_ar: 'غداء المكتب، عشاء العائلة، رغبات منتصف الليل — توصيل في الخوير.',
        metaTitle: 'Wow Pizza Al Khuwair | Pizza Delivery in Al Khuwair, Muscat | wowpizza.world',
        metaDescription: 'Order pizza delivery in Al Khuwair, Muscat from Wow Pizza. Fresh pizzas made to order and delivered fast. Call +968 92111946 or order online at wowpizza.world.',
        url: 'https://wowpizza.world/al-khuwair',
        address: 'Al Khuwair, Muscat, Oman',
        address_ar: 'الخوير، مسقط، عُمان',
        phones: [{ display: '+968 92111946', raw: '+96892111946' }],
        mapEmbed: 'https://maps.google.com/maps?q=Al+Khuwair+Muscat+Oman&output=embed',
        schema: KHUWAIR_SCHEMA,
        description: [
          'Al Khuwair is one of Muscat\'s busiest commercial and residential districts, and Wow Pizza Al Khuwair makes it easy to get a great pizza without the wait. We\'re popular with office crowds at lunch and families in the evening — and our delivery covers the wider Al Khuwair area.',
          'Every pizza is made fresh to order — no reheating, no shortcuts. Choose from our classic range or try our signature house specials. Whether you\'re ordering for one or feeding a crowd, we\'ll have it at your door fast.',
          'Call us on +968 92111946 or place your order online at wowpizza.world. Open Saturday through Thursday 11:00 AM – 4:00 AM and Friday 1:00 PM – 4:00 AM.',
        ],
      }}
    />
  );
}
